import * as Tabs from '@radix-ui/react-tabs';
import { CheckSquare } from '@styled-icons/boxicons-regular';
import {
  Coffee,
  Dribbble,
  Feather,
  Flag,
  Gift,
  Smile,
  Truck,
  User,
} from '@styled-icons/feather';
import { useEffect, useRef, useState } from 'react';
import selectMediaStyles from '../SelectMedia.module.css';
import styles from './SelectMediaEmoji.module.css';

const getEmojisAsync = async (group?: string, keyword?: string) => {
  const emojisByGroup = import(
    'unicode-emoji-json/data-by-group.json'
  ) as unknown as Promise<
    Record<
      string,
      {
        emoji: string
        name: string
        slug: string
      }[]
    >
  >;

  if (keyword) {
    const keywordsSet = (await import('emojilib')) as unknown as Record<
      string,
      string[]
    >;

    return Object.values(await emojisByGroup)
      .flat(1)
      .filter((e) => {
        const lowerCasedKeyword = keyword.toLowerCase();
        if (e.name?.includes(lowerCasedKeyword)) return true;
        return keywordsSet[e.emoji]?.some((k) => k.includes(lowerCasedKeyword));
      })
      .map((e) => e.emoji);
  }

  if (group) {
    return (await emojisByGroup)[group].map((e) => e.emoji);
  }

  // returns empty when neither keyword nor group are provided
  return [];
};

const emojiGroups = [
  {
    title: 'Smileys & Emotion',
    icon: <Smile size={20} />,
  },
  { title: 'People & Body', icon: <User size={20} /> },
  {
    title: 'Animals & Nature',
    icon: <Feather size={20} />,
  },
  { title: 'Symbols', icon: <CheckSquare size={20} /> },
  {
    title: 'Food & Drink',
    icon: <Coffee size={20} />,
  },
  { title: 'Activities', icon: <Dribbble size={20} /> },
  {
    title: 'Travel & Places',
    icon: <Truck size={20} />,
  },
  { title: 'Objects', icon: <Gift size={20} /> },
  { title: 'Flags', icon: <Flag size={20} /> },
];

export interface SelectMediaEmojiProps {
  onSelect: (emoji: string) => void
}

export function SelectMediaEmoji(props: SelectMediaEmojiProps) {
  const { onSelect } = props;

  const [emojis, setEmojis] = useState<string[]>([]);
  const getEmojis = (...args: Parameters<typeof getEmojisAsync>) => {
    getEmojisAsync(...args).then(setEmojis);
  };

  useEffect(() => getEmojis(emojiGroups[0].title), []);

  const formRef = useRef<HTMLFormElement>(null);

  return (
    <div>
      <form ref={formRef} className={selectMediaStyles.filter} onSubmit={(e) => e.preventDefault()}>
        <input
          className={selectMediaStyles.input}
          type="text"
          placeholder="Search emoji..."
          onChange={(e) => getEmojis(undefined, e.target.value)}
        />
      </form>
      <Tabs.Root
        defaultValue={emojiGroups[0].title}
        onValueChange={(group) => {
          getEmojis(group);
          if (formRef.current) formRef.current.reset();
        }}
      >
        <Tabs.List className={styles.tabList}>
          {emojiGroups.map((g) => (
            <Tabs.Trigger
              key={g.title}
              value={g.title}
              className={styles.tabTrigger}
            >
              {g.icon}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        {emojiGroups.map((g) => (
          <Tabs.Content
            key={g.title}
            value={g.title}
            className={styles.tabContent}
          >
            {g.title}
          </Tabs.Content>
        ))}
      </Tabs.Root>
      <div className={styles.emojiList}>
        {emojis.map((e) => (
          <span
            role="none"
            key={e}
            className={styles.emoji}
            onClick={() => onSelect(e)}
          >
            {e}
          </span>
        ))}
      </div>
    </div>
  );
}
